import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useImportStore } from '@/stores/importStore';
import { useProvider } from '@/hooks/useProvider';
import { useChannelTypes } from '@/hooks/useChannelTypes';
import { usePlans } from '@/hooks/usePlans';
import { useCategories } from '@/hooks/useCategories';
import { ValidationService } from '@/services/import/validation';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { 
  sequentialImportSchema, 
  nonSequentialImportSchema,
  ImportFormData,

} from '../../types/import';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { parseImportData } from '../../utils/import';
import { Badge } from '@/components/ui/badge';
import { Category } from '@/features/categories/types';
import { importService } from '@/services/import/importService';
import { sequentialImportService } from '@/services/import/sequentialImportService';
import { BusinessError } from '@/features/channels/utils/errors';
import { supabase } from '@/lib/supabase';

// Create a unique ID for logging purposes
const FORM_STORE_ID = `ImportForm_${Date.now()}`;

interface ImportFormProps {
  providerId: number;
  versionId: number;
  isSequential: boolean;
}

export function ImportForm({ 
  providerId, 
  versionId, 
  isSequential 
}: ImportFormProps) {
  const { 
    updateFormData, 
    setPreviewData, 
    goToNextStep,
    addErrors,
    clearErrors,
    completeReset,
    formData: persistedFormData
  } = useImportStore();

  const { data: provider, isLoading: isProviderLoading } = useProvider(providerId);
  const { data: types, isLoading: isTypesLoading } = useChannelTypes(providerId, versionId);
  const { data: plans, isLoading: isPlansLoading } = usePlans(providerId, versionId);
  const { data: categories, isLoading: isCategoriesLoading } = useCategories(providerId, versionId);

  const isLoading = isProviderLoading || isTypesLoading || isPlansLoading || isCategoriesLoading;

  // Get default channel type
  const defaultType = useMemo(() => {
    return types?.find(type => type.is_default);
  }, [types]);

  // Initialize form with appropriate schema and persisted data
  const form = useForm<ImportFormData>({
    resolver: zodResolver(isSequential ? sequentialImportSchema : nonSequentialImportSchema) as unknown as Resolver<ImportFormData>,
    defaultValues: {
      providerId,
      versionId,
      format: persistedFormData?.format || 'name_first',
      data: persistedFormData?.data || '',
      planIds: persistedFormData?.planIds || [],
      typeId: persistedFormData?.typeId || defaultType?.id,
      nextAvailableNumber: persistedFormData?.nextAvailableNumber,
      categoryId: persistedFormData?.categoryId,
      parentCategoryId: persistedFormData?.parentCategoryId,
      subCategoryId: persistedFormData?.subCategoryId,
      is_local: persistedFormData?.is_local || false,
      is_duplicate: persistedFormData?.is_duplicate || false
    }
  });

  // Set initial category states from persisted data
  useEffect(() => {
    if (persistedFormData) {
      setSelectedParentCategory(persistedFormData.parentCategoryId || null);
      setSelectedSubCategory(persistedFormData.subCategoryId || null);
    }
  }, [persistedFormData]);

  // Add effect to monitor form state
  useEffect(() => {
    console.log('Form mounted with values:', form.getValues());
  }, []);

  // Monitor form changes
  useEffect(() => {
    const subscription = form.watch((value) => {
      console.log('Form changed:', value);
      updateFormData(value as ImportFormData);
    });
    return () => subscription.unsubscribe();
  }, [form.watch, updateFormData]);

  // Clear plan selections when provider changes to enforce provider isolation
  useEffect(() => {
    // Reset planIds when provider changes to prevent cross-provider contamination
    form.setValue('planIds', []);
  }, [providerId]);

  // Enhanced validation to ensure plans are from the current provider AND version
  useEffect(() => {
    const validatePlans = async () => {
      if (form.getValues('planIds')?.length > 0 && versionId && providerId) {
        const selectedPlanIds = form.getValues('planIds');
        
        try {
          // First check that all plans belong to the current provider
          const { data: providerPlans, error: providerError } = await supabase
            .from('plans')
            .select('id, name, provider_id, version_id')
            .in('id', selectedPlanIds);
            
          if (providerError) {
            console.error('Error checking plan provider:', providerError);
            return;
          }
          
          // Filter plans from other providers (critical provider isolation check)
          const plansFromOtherProviders = providerPlans?.filter(plan => plan.provider_id !== providerId);
          
          if (plansFromOtherProviders?.length > 0) {
            // This is a critical error - plans from other providers should never be selected
            toast.error(`${plansFromOtherProviders.length} selected plans are from different providers and have been removed.`);
            
            console.error('Critical: Plans from other providers detected:', 
              plansFromOtherProviders.map(p => `${p.name} (ID: ${p.id}, Provider: ${p.provider_id})`)
            );
            
            // Remove these plans immediately to maintain provider isolation
            const validProviderPlanIds = providerPlans
              .filter(plan => plan.provider_id === providerId)
              .map(plan => plan.id);
              
            form.setValue('planIds', validProviderPlanIds);
            return;
          }
          
          // Now check version-specific plans
          const plansFromOtherVersions = providerPlans?.filter(plan => plan.version_id !== versionId);
          
          if (plansFromOtherVersions?.length > 0) {
            toast.warning(`${plansFromOtherVersions.length} selected plans are from different versions and will be ignored in import.`);
            
            console.warn('Plans from other versions will be ignored:', 
              plansFromOtherVersions.map(p => `${p.name} (ID: ${p.id}, Version: ${p.version_id})`)
            );
          }
        } catch (error) {
          console.error('Error validating plans:', error);
        }
      }
    };
    
    validatePlans();
  }, [form.watch('planIds'), versionId, providerId]);

  // Update validation service to use default type
  const onSubmit = async (data: ImportFormData) => {
    console.log('Starting import submission with data:', data);
    
    try {
      clearErrors();
      const validationService = new ValidationService({
        providerId,
        versionId,
        isSequential,
        channelTypes: (types || []).map(type => ({
          ...type,
          display_name: type.name,
          is_default: type.is_default || false,
          is_sequence: type.is_sequence || false,
          provider_id: type.provider_id || 0,
          version_id: type.version_id || 0
        })),
        defaultTypeId: defaultType?.id,
        is_local: data.is_local || false,
        is_duplicate: data.is_duplicate || false
      });

      console.log('Parsing import data with format:', data.format);
      // Parse the import data
      const { data: parsedData, errors: parseErrors } = parseImportData(
        data.data,
        data.format,
        (types || []).map(type => ({
          id: type.id,
          name: type.name,
          is_default: type.is_default || false,
          is_sequence: type.is_sequence || false
        }))
      );
      console.log('Parsed data result:', { parsedData, parseErrors });

      if (parseErrors.length > 0) {
        console.log('Parse errors found:', parseErrors);
        addErrors(parseErrors);
        parseErrors.forEach(error => {
          if (error.field === 'format') {
            toast.error(error.message);
          }
        });
        return;
      }

      // For sequential providers, get the sequence type first
      let sequenceTypeId: number | undefined;
      if (isSequential) {
        try {
          sequenceTypeId = await sequentialImportService.getSequenceTypeId(providerId, versionId);
          console.log('Got sequence type ID:', sequenceTypeId);
        } catch (error) {
          console.error('Failed to get sequence type:', error);
          toast.error("Failed to get sequence type for this provider");
          return;
        }
      }

      console.log('Starting data validation');
      // Validate the parsed data
      const result = await validationService.validateData(parsedData);
      console.log('Validation result:', result);

      if (!result.isValid) {
        console.log('Validation failed with errors:', result.errors);
        
        // Group errors by type for better presentation
        const formatErrors = result.errors.filter(e => e.field === 'format');
        const nameErrors = result.errors.filter(e => e.field === 'name');
        const numberErrors = result.errors.filter(e => e.field === 'number');
        
        // Show format errors
        if (formatErrors.length > 0) {
          toast.error(formatErrors[0].message);
        }
        
        // Show name errors (especially duplicates)
        if (nameErrors.length > 0) {
          const duplicateNames = nameErrors.filter(e => e.message.includes('already exists') || e.message.includes('Duplicate'));
          
          if (duplicateNames.length > 0) {
            // Display first few duplicate errors, avoid overwhelming the user
            const displayCount = Math.min(3, duplicateNames.length);
            const remainingCount = duplicateNames.length - displayCount;
            
            const errorMessages = duplicateNames.slice(0, displayCount).map(e => e.message).join(', ');
            const suffix = remainingCount > 0 ? ` ...and ${remainingCount} more duplicate name errors` : '';
            toast.error(`Duplicate Channel Names: ${errorMessages}${suffix}`);
          } else if (nameErrors.length > 0) {
            // Show other name validation errors
            toast.error(nameErrors[0].message);
          }
        }
        
        // Show number errors (especially duplicates)
        if (numberErrors.length > 0) {
          const duplicateNumbers = numberErrors.filter(e => e.message.includes('already exists') || e.message.includes('Duplicate'));
          
          if (duplicateNumbers.length > 0) {
            // Display first few duplicate errors, avoid overwhelming the user
            const displayCount = Math.min(3, duplicateNumbers.length);
            const remainingCount = duplicateNumbers.length - displayCount;
            
            const errorMessages = duplicateNumbers.slice(0, displayCount).map(e => e.message).join(', ');
            const suffix = remainingCount > 0 ? ` ...and ${remainingCount} more duplicate number errors` : '';
            toast.error(`Duplicate Channel Numbers: ${errorMessages}${suffix}`);
          } else if (numberErrors.length > 0) {
            // Show other number validation errors
            toast.error(numberErrors[0].message);
          }
        }
        
        // If there are other types of errors not caught above
        const otherErrors = result.errors.filter(
          e => !formatErrors.includes(e) && !nameErrors.includes(e) && !numberErrors.includes(e)
        );
        
        if (otherErrors.length > 0) {
          toast.error(otherErrors[0].message);
        }

        // Still add errors to the store for reference
        addErrors(result.errors);
        return;
      }

      console.log('Preparing preview data');
      // Prepare preview data
      const previewData = parsedData.map(channel => ({
        ...channel,
        categoryId: selectedSubCategory || selectedParentCategory || undefined,
        planIds: data.planIds,
        numbers: isSequential 
          ? [{
              typeId: sequenceTypeId!,
              number: '',
              isSequence: true,
              is_default: true
            }]
          : channel.numbers.map(num => ({
              typeId: num.typeId,
              // Ensure proper handling of null/empty values
              number: !num.number || num.number.trim() === '' ? '*' : num.number.trim(),
              isSequence: false,
              is_default: types?.find(t => t.id === num.typeId)?.is_default || false
            }))
      }));

      console.log('Preview data prepared:', previewData);
      
      try {
        logger.info(LOG_CONTEXTS.IMPORT, `Setting preview data in ${FORM_STORE_ID}`, { 
          dataCount: previewData.length || 0,
          hasData: !!previewData
        });
        
        // Set preview data
        setPreviewData(previewData);
        
        console.log('Moving to next step');
        // Only move to the preview step
        goToNextStep();
      } catch (error) {
        console.error('Error setting preview data:', error);
        toast.error("Failed to display preview data. Please try again.");
      }
    } catch (error) {
      console.error('Import submission failed:', error);
      if (error instanceof BusinessError) {
        toast.error(error.message);
      } else {
        toast.error("An unexpected error occurred");
      }
    }
  };

  // Add state for category hierarchy
  const [selectedParentCategory, setSelectedParentCategory] = useState<number | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<number | null>(null);

  // Organize categories into hierarchy
  const categoryHierarchy = useMemo(() => {
    if (!categories) return { parents: [], children: new Map() };

    const parents = categories.filter(c => !c.parent_id);
    const children = categories.reduce((acc, cat) => {
      if (cat.parent_id) {
        const siblings = acc.get(cat.parent_id) || [];
        acc.set(cat.parent_id, [...siblings, cat]);
      }
      return acc;
    }, new Map<number, Category[]>());

    return { parents, children };
  }, [categories]);

  // Update form when category selection changes
  useEffect(() => {
    if (selectedSubCategory) {
      form.setValue('categoryId', selectedSubCategory);
    } else if (selectedParentCategory) {
      form.setValue('categoryId', selectedParentCategory);
    } else {
      form.setValue('categoryId', undefined);
    }
  }, [selectedParentCategory, selectedSubCategory]);

  useEffect(() => {
    const fetchLastNumber = async () => {
      if (provider?.has_sequence_numbers && providerId && versionId) {
        try {
          console.log('Fetching last number for:', {
            providerId,
            versionId
          });
          const lastNumber = await importService.getLastSequenceNumber(
            providerId,
            versionId
          );
          console.log('Got last number:', lastNumber);
          form.setValue('lastNumber', lastNumber);
          
          // Also store the next number for display
          const nextNumber = String(Number(lastNumber) + 1);
          form.setValue('nextAvailableNumber', nextNumber);
        } catch (error) {
          console.error('Failed to get last sequence number:', error);
        }
      }
    };

    fetchLastNumber();
    
    // Create an interval to periodically refresh the number
    // This ensures accurate numbers after imports or other changes
    const refreshInterval = setInterval(fetchLastNumber, 5000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [provider?.has_sequence_numbers, providerId, versionId]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // Show error if no default type exists
  if (!defaultType) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">
          Please set up channel types before importing channels.
        </p>
      </div>
    );
  }

  const formatExampleData = (types: Array<{ id: number; name: string }>, format: 'name_first' | 'number_first') => {
    const numbers = types.map(() => '123');
    return format === 'name_first'
      ? `Channel Name | ${numbers.join(' | ')}`
      : `${numbers.join(' | ')} | Channel Name`;
  };

  const renderImportDataField = () => (
    <FormField
      control={form.control}
      name="data"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Import Data</FormLabel>
          <FormControl>
            <div className="space-y-2">
              <Textarea
                placeholder="Paste your data here..."
                className="min-h-[200px] font-mono"
                {...field}
              />
              {!isSequential && types && (
                <div className="text-sm text-muted-foreground">
                  <p>Example format:</p>
                  <code>{formatExampleData(types, form.watch('format'))}</code>
                </div>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="space-y-3">
            {!isSequential && (
              <FormField
                control={form.control}
                name="format"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Format</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name_first">Name First</SelectItem>
                          <SelectItem value="number_first">Number First</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="space-y-3">
              <FormField
                control={form.control}
                name="parentCategoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Parent Category</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        setSelectedParentCategory(value ? parseInt(value) : null);
                        setSelectedSubCategory(null);
                        field.onChange(value ? parseInt(value) : undefined);
                      }}
                      value={selectedParentCategory?.toString()}
                    >
                      <FormControl>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="null">None</SelectItem>
                        {categoryHierarchy.parents.map(category => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              {selectedParentCategory && (
                <FormField
                  control={form.control}
                  name="subCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Sub Category</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          setSelectedSubCategory(value ? parseInt(value) : null);
                          field.onChange(value ? parseInt(value) : undefined);
                        }}
                        value={selectedSubCategory?.toString()}
                      >
                        <FormControl>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select sub category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="null">None</SelectItem>
                          {categoryHierarchy.children.get(selectedParentCategory)?.map((category: Category) => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Add Local Channel Checkbox */}
            <FormField
              control={form.control}
              name="is_local"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium">
                      Local Channel
                    </FormLabel>
                    <FormDescription className="text-xs text-muted-foreground">
                      Allow duplicate channel names (for region-specific channels)
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Add Duplicate Channel Checkbox */}
            <FormField
              control={form.control}
              name="is_duplicate"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium">
                      Allow Duplicates
                    </FormLabel>
                    <FormDescription className="text-xs text-muted-foreground">
                      Allow duplicate channel numbers and names (for intentional duplicates)
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
          </div>

          <div>
            <div className="space-y-3">
              <label className="text-sm font-medium">Channel Types</label>
              <div className="border rounded-md p-3" style={{ minHeight: '160px' }}>
                {isTypesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                  </div>
                ) : types?.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    No channel types available.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {types?.map(type => (
                      <div key={type.id} className="flex items-center justify-between text-sm py-0.5">
                        <span className="font-medium">{type.name}</span>
                        <div className="flex gap-1">
                          {type.is_default && (
                            <Badge variant="secondary" className="text-xs px-1">Default</Badge>
                          )}
                          <Badge variant="outline" className="text-xs px-1">
                            {type.is_sequence ? 'Sequential' : 'Manual'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <FormField
              control={form.control}
              name="planIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plans</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <ScrollArea className="h-32 rounded-md border">
                        <div className="p-4">
                          {plans?.map(plan => (
                            <div key={plan.id} className="flex items-center space-x-2 py-1">
                              <Checkbox
                                checked={field.value?.includes(plan.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([...(field.value || []), plan.id]);
                                  } else {
                                    field.onChange(field.value?.filter(id => id !== plan.id));
                                  }
                                }}
                              />
                              <label className="text-sm font-medium leading-none">
                                {plan.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-1 sm:col-span-2 xl:col-span-3">
            {renderImportDataField()}
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              // Call completeReset to truly reset everything including localStorage
              completeReset();
              // Reset the form to initial values
              form.reset({
                providerId,
                versionId,
                format: 'name_first',
                data: '',
                planIds: [],
                typeId: defaultType?.id,
                categoryId: undefined,
                parentCategoryId: undefined,
                subCategoryId: undefined,
                is_local: false,
                is_duplicate: false
              });
              // Reset category selections
              setSelectedParentCategory(null);
              setSelectedSubCategory(null);
              toast.info("Form has been completely reset");
            }}
          >
            Reset Form
          </Button>
          
          <Button 
            type="submit" 
            disabled={!form.watch('data')?.trim()}
          >
            Preview Import
          </Button>
        </div>
      </form>
    </Form>
  );
}
